/**
 * AddCourierCredential Page
 * 
 * Standalone page for adding new courier credentials
 * Includes comprehensive form with validation and API integration
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { courierCredentialApi, authHelpers } from '../services/api'; // Adjust path as needed
import { message } from 'antd';

// Courier type options
const COURIER_TYPE_OPTIONS = ['Shiprocket', 'Delhivery', 'Blue Dart', 'XpressBess'];

// Weight options (in grams)
const WEIGHT_OPTIONS = ['250', '500', '750', '1000', '1500', '2000'];

// Post office options (you can fetch these from API or expand this list)
const POST_OFFICE_OPTIONS = [
  'Noida Sector 62',
  'Delhi Central',
  'Gurgaon Cyber City',
  'Mumbai Central',
  'Bangalore Whitefield',
  'Hyderabad HITEC City',
  'Chennai Anna Nagar',
  'Pune Hinjewadi',
  'Kolkata Salt Lake',
  'Ahmedabad Satellite',
];

export default function AddCourierCredential() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    courierType: '',
    apiEmail: '',
    apiPassword: '',
    apiToken: '', // Added API Token field
    orderHeight: '',
    orderWidth: '',
    orderLength: '',
    orderWeight: '',
    pickupLocation: '',
    pickupAddress: '',
    areaPinCode: '',
    postOffice: '',
    district: '',
    state: '',
  });

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.courierType) {
        message.warning('Please select a courier type');
        return;
      }
      if (!formData.apiEmail.trim()) {
        message.warning('Please enter API email');
        return;
      }
      if (!formData.apiPassword.trim()) {
        message.warning('Please enter API password');
        return;
      }
      if (!formData.orderHeight || !formData.orderWidth || !formData.orderLength) {
        message.warning('Please enter all order dimensions');
        return;
      }
      if (!formData.orderWeight) {
        message.warning('Please select order weight');
        return;
      }
      if (!formData.areaPinCode.trim()) {
        message.warning('Please enter area pin code');
        return;
      }

      setLoading(true);

      // Get current user ID
      const currentUser = authHelpers.getCurrentUser();
      const createdById = currentUser?.id || 1;

      // Prepare data for API
      const apiData = {
        campaignName: formData.courierType,
        courierType: formData.courierType.toUpperCase().replace(' ', '_'),
        apiEmail: formData.apiEmail.trim(),
        apiPassword: formData.apiPassword.trim(),
        apiToken: formData.apiToken.trim() || null, // Include API Token
        shippingMode: 'SURFACE', // Default to SURFACE
        orderHeight: parseInt(formData.orderHeight),
        orderWidth: parseInt(formData.orderWidth),
        orderLength: parseInt(formData.orderLength),
        orderWeight: parseInt(formData.orderWeight),
        pickupLocation: formData.pickupLocation.trim() || null,
        pickupAddress: formData.pickupAddress.trim() || null,
        areaPinCode: formData.areaPinCode.trim(),
        postOffice: formData.postOffice || null,
        district: formData.district.trim() || null,
        state: formData.state.trim() || null,
        status: true,
        createdById: createdById,
      };

      // Call API
      await courierCredentialApi.createCredential(apiData);

      message.success('Courier credential created successfully!');

      // Navigate back to courier API settings page
      navigate('/settings/courier-api'); // Adjust route as needed
    } catch (err) {
      console.error('Error creating courier credential:', err);
      message.error(err.message || err.error || 'Failed to create courier credential. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel/back
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/settings/courier-api'); // Adjust route as needed
    }
  };

  const isDark = currentTheme.textColor === '#FFFFFF' || currentTheme.color === '#0f172a';

  return (
    <div className={`min-h-screen p-8 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : ''}`} style={{ background: isDark ? undefined : `linear-gradient(135deg, ${currentTheme.color}10 0%, ${currentTheme.color}05 50%, #ffffff 100%)` }}>
      {isDark && (
        <style>
          {`
            input:-webkit-autofill,
            input:-webkit-autofill:hover, 
            input:-webkit-autofill:focus, 
            input:-webkit-autofill:active {
              -webkit-box-shadow: 0 0 0px 1000px #0f172a inset !important;
              -webkit-text-fill-color: white !important;
              transition: background-color 5000s ease-in-out 0s;
            }
          `}
        </style>
      )}
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <button
            onClick={handleCancel}
            className={`mb-4 flex items-center gap-2 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Courier API
          </button>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg shadow-md" style={{ backgroundColor: currentTheme.color }}>
              <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Add Courier Credential</h1>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Configure your courier API credentials and settings</p>
            </div>
          </div>
        </div>

        {/* Form Container */}
        <div className={`rounded-2xl shadow-xl overflow-hidden transition-all duration-300 ${isDark ? 'bg-[#1e293b] border border-white/10' : 'bg-white border-white/50'}`}>
          <div className="p-8">
            {/* Select Courier Type Section */}
            <div className="mb-10">
              <h2 className={`text-xl font-semibold mb-6 pb-2 border-b-2 ${isDark ? 'text-white' : 'text-gray-800'}`} style={{ borderBottomColor: `${currentTheme.color}20` }}>
                Select Courier Type <span className="text-red-500">*</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <div className="relative">
                    <select
                      value={formData.courierType}
                      onChange={(e) => handleChange('courierType', e.target.value)}
                      className={`w-full h-12 pl-12 pr-10 border-2 rounded-lg focus:outline-none appearance-none transition-all ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white text-gray-700 border-gray-300'}`}
                      style={{ borderColor: formData.courierType ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB') }}
                    >
                      <option value="">Please select</option>
                      {COURIER_TYPE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt} className={isDark ? 'bg-[#1e293b]' : ''}>{opt}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6 0a1 1 0 001 1h2a1 1 0 001-1m0 0h.01M5 7h2m-2 3h3m-3 3h3m6-6h.01M15 13h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <svg className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Credentials Section */}
            <div className="mb-10">
              <h2 className={`text-xl font-semibold mb-6 pb-2 border-b-2 ${isDark ? 'text-white' : 'text-gray-800'}`} style={{ borderBottomColor: `${currentTheme.color}20` }}>
                Credentials
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    API Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.apiEmail}
                    onChange={(e) => handleChange('apiEmail', e.target.value)}
                    placeholder="Enter email"
                    className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                    style={{ 
                      borderColor: formData.apiEmail ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                      WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                      WebkitTextFillColor: isDark ? 'white' : 'inherit'
                    }}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    API Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={formData.apiPassword}
                    onChange={(e) => handleChange('apiPassword', e.target.value)}
                    placeholder="Enter password"
                    className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                    style={{ 
                      borderColor: formData.apiPassword ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                      WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                      WebkitTextFillColor: isDark ? 'white' : 'inherit'
                    }}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    API Token
                  </label>
                  <input
                    type="text"
                    value={formData.apiToken}
                    onChange={(e) => handleChange('apiToken', e.target.value)}
                    placeholder="Enter API token (optional)"
                    className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                    style={{ 
                      borderColor: formData.apiToken ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                      WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                      WebkitTextFillColor: isDark ? 'white' : 'inherit'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Order Details Section */}
            <div className="mb-10">
              <h2 className={`text-xl font-semibold mb-6 pb-2 border-b-2 ${isDark ? 'text-white' : 'text-gray-800'}`} style={{ borderBottomColor: `${currentTheme.color}20` }}>
                Order Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {['orderHeight', 'orderWidth', 'orderLength'].map((dim) => (
                  <div key={dim}>
                    <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {dim.replace('order', '')} (cm) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData[dim]}
                      onChange={(e) => handleChange(dim, e.target.value)}
                      placeholder="0"
                      className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                      style={{ 
                        borderColor: formData[dim] ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                        WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                        WebkitTextFillColor: isDark ? 'white' : 'inherit'
                      }}
                    />
                  </div>
                ))}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Order Weight <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={formData.orderWeight}
                      onChange={(e) => handleChange('orderWeight', e.target.value)}
                      className={`w-full h-12 pl-4 pr-10 border-2 rounded-lg focus:outline-none appearance-none transition-all ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white text-gray-700 border-gray-300'}`}
                      style={{ borderColor: formData.orderWeight ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB') }}
                    >
                      <option value="">Please select</option>
                      {WEIGHT_OPTIONS.map((opt) => (
                        <option key={opt} value={opt} className={isDark ? 'bg-[#1e293b]' : ''}>{opt} GM</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pickup Location Section */}
            <div>
              <h2 className={`text-xl font-semibold mb-6 pb-2 border-b-2 ${isDark ? 'text-white' : 'text-gray-800'}`} style={{ borderBottomColor: `${currentTheme.color}20` }}>
                Add Pickup Location
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Enter pickup location
                  </label>
                  <input
                    type="text"
                    value={formData.pickupLocation}
                    onChange={(e) => handleChange('pickupLocation', e.target.value)}
                    placeholder="Enter location"
                    className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                    style={{ 
                      borderColor: formData.pickupLocation ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                      WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                      WebkitTextFillColor: isDark ? 'white' : 'inherit'
                    }}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.pickupAddress}
                    onChange={(e) => handleChange('pickupAddress', e.target.value)}
                    placeholder="Enter address"
                    className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                    style={{ 
                      borderColor: formData.pickupAddress ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                      WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                      WebkitTextFillColor: isDark ? 'white' : 'inherit'
                    }}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Area pin code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.areaPinCode}
                    onChange={(e) => handleChange('areaPinCode', e.target.value)}
                    placeholder="201014"
                    maxLength="6"
                    className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                    style={{ 
                      borderColor: formData.areaPinCode ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                      WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                      WebkitTextFillColor: isDark ? 'white' : 'inherit'
                    }}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Post Office <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={formData.postOffice}
                      onChange={(e) => handleChange('postOffice', e.target.value)}
                      className={`w-full h-12 pl-4 pr-10 border-2 rounded-lg focus:outline-none appearance-none transition-all ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white text-gray-700 border-gray-300'}`}
                      style={{ borderColor: formData.postOffice ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB') }}
                    >
                      <option value="">Please select</option>
                      {POST_OFFICE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt} className={isDark ? 'bg-[#1e293b]' : ''}>{opt}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    District <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(e) => handleChange('district', e.target.value)}
                    placeholder="Enter district"
                    className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                    style={{ 
                      borderColor: formData.district ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                      WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                      WebkitTextFillColor: isDark ? 'white' : 'inherit'
                    }}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="Enter state"
                    className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] text-white placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                    style={{ 
                      borderColor: formData.state ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'),
                      WebkitBoxShadow: isDark ? '0 0 0px 1000px #0f172a inset' : 'none',
                      WebkitTextFillColor: isDark ? 'white' : 'inherit'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Footer */}
          <div className={`px-8 py-6 border-t flex justify-end gap-4 transition-colors ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className={`px-8 py-3 rounded-xl border-2 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-10 py-3 rounded-xl text-white font-semibold transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:opacity-90"
              style={{ backgroundColor: currentTheme.color }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Submit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}